import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Trophy, Users, TrendingUp, TrendingDown, Minus, Crown, Medal, 
    Target, Zap, Download, Calendar, ChevronDown, ChevronUp,
    Award, Star, ArrowUp, ArrowDown, Clock, Share2, UserPlus, X, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEngagementPage() {
    const { competitionId } = useParams();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selectedDate, setSelectedDate] = useState('all');
    const [activeTab, setActiveTab] = useState('daily');
    const [expandedRaces, setExpandedRaces] = useState({});
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetition, setSelectedCompetition] = useState(competitionId || '');
    const [invitedUsers, setInvitedUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [showInvitePanel, setShowInvitePanel] = useState(false);
    
    const isAdmin = user?.is_admin;
    const contentRef = useRef(null);

    const fetchCompetitions = useCallback(async () => {
        try {
            const token = getToken();
            if (isAdmin) {
                const res = await axios.get(`${API_URL}/api/admin/competitions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCompetitions(res.data.competitions || []);
                if (res.data.competitions?.length > 0) {
                    setSelectedCompetition(prev => prev || res.data.competitions[0].id);
                }
            } else {
                // Non-admin: fetch only invited competitions
                const res = await axios.get(`${API_URL}/api/engagement/my-access`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCompetitions(res.data.competitions || []);
                if (res.data.competitions?.length > 0) {
                    setSelectedCompetition(prev => prev || res.data.competitions[0].id);
                }
            }
        } catch (err) {
            console.error('Failed to fetch competitions:', err);
            toast.error('Failed to load competitions');
        }
    }, [isAdmin]);

    const fetchEngagementData = useCallback(async () => {
        if (!selectedCompetition) return;
        
        setLoading(true);
        try {
            const token = getToken();
            const dateParam = activeTab === 'daily' && selectedDate !== 'all' ? `?date=${selectedDate}` : '';
            const res = await axios.get(
                `${API_URL}/api/admin/competitions/${selectedCompetition}/engagement${dateParam}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setData(res.data);
            
            // Auto-select first date for daily tab
            if (activeTab === 'daily' && selectedDate === 'all' && res.data.dates?.length > 0) {
                setSelectedDate(res.data.dates[0]);
            }
        } catch (err) {
            console.error('Failed to fetch engagement data:', err);
            toast.error('Failed to load engagement data');
        } finally {
            setLoading(false);
        }
    }, [selectedCompetition, selectedDate, activeTab]);

    useEffect(() => {
        if (!isAuthenticated) {
            toast.error('Login required');
            navigate('/competitions');
            return;
        }
        fetchCompetitions();
    }, [isAuthenticated, navigate, fetchCompetitions]);

    // Fetch invited users list (admin only)
    const fetchInvites = useCallback(async () => {
        if (!isAdmin || !selectedCompetition) return;
        try {
            const token = getToken();
            const res = await axios.get(
                `${API_URL}/api/admin/competitions/${selectedCompetition}/engagement/invites`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInvitedUsers(res.data.invites || []);
        } catch (err) {
            console.error('Failed to fetch invites:', err);
        }
    }, [isAdmin, selectedCompetition]);

    // Fetch all users for invite dropdown (admin only)
    const fetchAllUsers = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllUsers(res.data.users || []);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (selectedCompetition) {
            fetchEngagementData();
            fetchInvites();
        }
    }, [selectedCompetition, fetchEngagementData, fetchInvites]);

    useEffect(() => {
        fetchAllUsers();
    }, [fetchAllUsers]);

    const handleInviteUser = async (userId) => {
        try {
            const token = getToken();
            await axios.post(
                `${API_URL}/api/admin/competitions/${selectedCompetition}/engagement/invite`,
                { user_id: userId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('User invited');
            fetchInvites();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to invite user');
        }
    };

    const handleRevokeUser = async (userId) => {
        try {
            const token = getToken();
            await axios.delete(
                `${API_URL}/api/admin/competitions/${selectedCompetition}/engagement/invite/${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Access revoked');
            fetchInvites();
        } catch (err) {
            toast.error('Failed to revoke access');
        }
    };

    const handleExportImage = async () => {
        if (!contentRef.current) return;
        
        try {
            toast.info('Generating image...');
            const canvas = await html2canvas(contentRef.current, {
                backgroundColor: '#0f172a',
                scale: 2,
                useCORS: true
            });
            
            const link = document.createElement('a');
            link.download = `racewise-engagement-${selectedDate || 'overall'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            
            toast.success('Image downloaded!');
        } catch (err) {
            console.error('Failed to export image:', err);
            toast.error('Failed to generate image');
        }
    };

    const [isExporting, setIsExporting] = useState(false);

    // Helper function to convert image URL to base64 data URL via backend proxy
    const imageToBase64 = async (imgUrl) => {
        try {
            // First try direct fetch
            const response = await fetch(imgUrl, { mode: 'cors' });
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            // If CORS fails, use backend proxy
            try {
                const token = getToken();
                const proxyResponse = await axios.get(
                    `${API_URL}/api/proxy/image?url=${encodeURIComponent(imgUrl)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                return proxyResponse.data.data;
            } catch (proxyError) {
                console.warn('Failed to convert image to base64:', imgUrl, proxyError);
                return null;
            }
        }
    };

    const handleExportAllRaceCards = async (races) => {
        if (!races || races.length === 0) {
            toast.error('No race cards to export');
            return;
        }

        setIsExporting(true);
        toast.info(`Preparing ${races.length} race cards for export...`);
        
        // First, expand ALL race cards
        const previousExpandedState = { ...expandedRaces };
        const allExpanded = {};
        races.forEach(race => {
            allExpanded[race.event_id] = true;
        });
        setExpandedRaces(allExpanded);
        
        // Wait for React to re-render
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Pre-convert all images to base64 to avoid CORS issues
        toast.info('Loading images...');
        const imageCache = new Map();
        const allImages = document.querySelectorAll('[id^="race-card-"] img');
        
        await Promise.all(Array.from(allImages).map(async (img) => {
            if (img.src && img.src.startsWith('http') && !imageCache.has(img.src)) {
                const base64 = await imageToBase64(img.src);
                if (base64) {
                    imageCache.set(img.src, base64);
                }
            }
        }));
        
        toast.info(`Downloading ${races.length} race cards...`);
        
        for (let i = 0; i < races.length; i++) {
            const race = races[i];
            const element = document.getElementById(`race-card-${race.event_id}`);
            
            if (!element) {
                console.warn(`Race card element not found for ${race.event_id}`);
                continue;
            }
            
            try {
                const canvas = await html2canvas(element, {
                    backgroundColor: '#0f172a',
                    scale: 2,
                    useCORS: true,
                    allowTaint: false,
                    logging: false,
                    imageTimeout: 5000,
                    onclone: (clonedDoc) => {
                        // Replace all images with base64 versions in the clone
                        const clonedElement = clonedDoc.getElementById(`race-card-${race.event_id}`);
                        if (clonedElement) {
                            clonedElement.style.display = 'block';
                            const clonedImages = clonedElement.querySelectorAll('img');
                            clonedImages.forEach(img => {
                                const base64 = imageCache.get(img.src);
                                if (base64) {
                                    img.src = base64;
                                }
                            });
                        }
                    }
                });
                
                const link = document.createElement('a');
                const raceName = race.event_title || `Race${race.race_number}`;
                link.download = `racewise-R${race.race_number}-${raceName.replace(/[^a-z0-9]/gi, '_')}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                
                // Small delay between downloads
                await new Promise(resolve => setTimeout(resolve, 400));
            } catch (err) {
                console.error(`Failed to export race card ${race.race_number}:`, err);
            }
        }
        
        // Restore original expanded state
        setExpandedRaces(previousExpandedState);
        setIsExporting(false);
        toast.success(`Exported ${races.length} race cards!`);
    };

    const toggleRaceExpanded = (eventId) => {
        setExpandedRaces(prev => ({
            ...prev,
            [eventId]: !prev[eventId]
        }));
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
        });
    };

    const getPositionChange = (history, raceIndex) => {
        if (raceIndex === 0) return 0;
        const current = history[raceIndex]?.position || 0;
        const previous = history[raceIndex - 1]?.position || 0;
        return previous - current; // Positive = moved up
    };

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-background p-4 md:p-8">
                <div className="max-w-7xl mx-auto space-y-6">
                    <Skeleton className="h-12 w-64" />
                    <Skeleton className="h-[400px] w-full" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-border">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-2 page-title">
                                <TrendingUp className="h-6 w-6 text-primary" />
                                Engagement Dashboard
                            </h1>
                            <p className="text-slate-400 mt-1">
                                Pick distributions & leaderboard progression
                            </p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
                                <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700">
                                    <SelectValue placeholder="Select competition" />
                                </SelectTrigger>
                                <SelectContent>
                                    {competitions.map(comp => (
                                        <SelectItem key={comp.id} value={comp.id}>
                                            {comp.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            {isAdmin && (
                                <Button 
                                    variant="outline" 
                                    onClick={() => setShowInvitePanel(!showInvitePanel)}
                                    className="gap-2"
                                    data-testid="invite-users-btn"
                                >
                                    <UserPlus className="h-4 w-4" />
                                    Invite
                                </Button>
                            )}
                            
                            <Button 
                                variant="outline" 
                                onClick={handleExportImage}
                                className="gap-2"
                            >
                                <Download className="h-4 w-4" />
                                Export
                            </Button>
                        </div>
                    </div>
                    
                    {/* Invite Panel (Admin Only) */}
                    {isAdmin && showInvitePanel && (
                        <div className="mt-4 bg-slate-800/80 rounded-lg p-4 border border-slate-700" data-testid="invite-panel">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <UserPlus className="h-4 w-4 text-primary" />
                                    Manage Access
                                </h3>
                                <Button variant="ghost" size="sm" onClick={() => setShowInvitePanel(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                            
                            {/* Add User */}
                            <div className="mb-3">
                                <Select onValueChange={(val) => handleInviteUser(val)}>
                                    <SelectTrigger className="w-full bg-slate-900 border-slate-600">
                                        <SelectValue placeholder="Select user to invite..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {allUsers
                                            .filter(u => !u.is_admin && !invitedUsers.find(inv => inv.user_id === u.id))
                                            .map(u => (
                                                <SelectItem key={u.id} value={u.id}>
                                                    {u.name} ({u.email})
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Current Invites */}
                            {invitedUsers.length > 0 ? (
                                <div className="space-y-2">
                                    {invitedUsers.map(inv => (
                                        <div key={inv.user_id} className="flex items-center justify-between bg-slate-900 rounded px-3 py-2">
                                            <div>
                                                <span className="text-sm text-white font-medium">{inv.user_name}</span>
                                                <span className="text-xs text-slate-400 ml-2">{inv.user_email}</span>
                                            </div>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={() => handleRevokeUser(inv.user_id)}
                                                className="text-red-400 hover:text-red-300 h-7 px-2"
                                                data-testid={`revoke-${inv.user_id}`}
                                            >
                                                <X className="h-3 w-3 mr-1" />
                                                Revoke
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-slate-500">No users invited yet. Only admins can currently view this page.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {data && (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <TabsList className="bg-slate-800">
                                <TabsTrigger value="daily" className="gap-2">
                                    <Calendar className="h-4 w-4" />
                                    Daily View
                                </TabsTrigger>
                                <TabsTrigger value="overall" className="gap-2">
                                    <Trophy className="h-4 w-4" />
                                    Overall Competition
                                </TabsTrigger>
                                <TabsTrigger value="summary" className="gap-2">
                                    <Award className="h-4 w-4" />
                                    Tournament Summary
                                </TabsTrigger>
                            </TabsList>
                            
                            {activeTab === 'daily' && data.dates?.length > 0 && (
                                <Select value={selectedDate} onValueChange={setSelectedDate}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder="Select date" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {data.dates.map(date => (
                                            <SelectItem key={date} value={date}>
                                                {formatDate(date)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        
                        {/* Content for export */}
                        <div ref={contentRef} className="space-y-6">
                            {/* Stats Bar */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <Card className="bg-slate-800/50 border-slate-700">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <Users className="h-5 w-5 text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold">{data.total_entries}</p>
                                                <p className="text-xs text-muted-foreground">Players</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                
                                <Card className="bg-slate-800/50 border-slate-700">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-amber-500/10">
                                                <Target className="h-5 w-5 text-amber-500" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold">{data.race_data?.length || 0}</p>
                                                <p className="text-xs text-muted-foreground">Races</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                
                                <Card className="bg-slate-800/50 border-slate-700">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-green-500/10">
                                                <Trophy className="h-5 w-5 text-green-500" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold">
                                                    {data.race_data?.filter(r => r.status === 'finished').length || 0}
                                                </p>
                                                <p className="text-xs text-muted-foreground">Finished</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                
                                <Card className="bg-slate-800/50 border-slate-700">
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-purple-500/10">
                                                <Zap className="h-5 w-5 text-purple-500" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold">{data.total_picks || 0}</p>
                                                <p className="text-xs text-muted-foreground">Total Picks</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                            
                            <TabsContent value="daily" className="mt-0 space-y-6">
                                {/* Download All Race Cards Button */}
                                <div className="flex justify-end">
                                    <Button 
                                        variant="outline"
                                        onClick={() => handleExportAllRaceCards(data.race_data)}
                                        disabled={isExporting}
                                        className="gap-2"
                                    >
                                        {isExporting ? (
                                            <>
                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                                Exporting...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="h-4 w-4" />
                                                Download All Race Cards
                                            </>
                                        )}
                                    </Button>
                                </div>
                                
                                {/* Race Cards with Pick Distribution */}
                                {data.race_data?.map((race, idx) => (
                                    <RaceEngagementCard 
                                        key={race.event_id}
                                        race={race}
                                        expanded={expandedRaces[race.event_id]}
                                        onToggle={() => toggleRaceExpanded(race.event_id)}
                                        raceCardId={`race-card-${race.event_id}`}
                                    />
                                ))}
                                
                                {/* Daily As It Happened */}
                                <AsItHappenedTable 
                                    data={data.as_it_happened}
                                    races={data.race_data}
                                    title="Daily Leaderboard Progression"
                                />
                            </TabsContent>
                            
                            <TabsContent value="overall" className="mt-0 space-y-6">
                                {/* Overall Competition View */}
                                <AsItHappenedTable 
                                    data={data.as_it_happened}
                                    races={data.race_data}
                                    title="Competition Leaderboard - As It Happened"
                                    showAllRaces
                                />
                            </TabsContent>
                            
                            <TabsContent value="summary" className="mt-0 space-y-6">
                                {/* Tournament Summary */}
                                <TournamentSummaryCard 
                                    data={data}
                                    formatDate={formatDate}
                                />
                            </TabsContent>
                        </div>
                    </Tabs>
                )}
            </div>
        </div>
    );
}

// Race Engagement Card Component
function RaceEngagementCard({ race, expanded, onToggle, raceCardId }) {
    const [expandedHorses, setExpandedHorses] = useState({});
    const top3 = race.distribution?.slice(0, 3) || [];
    const isFinished = race.status === 'finished';
    const winner = race.winner;
    
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', { 
            day: 'numeric', 
            month: 'short',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const toggleHorseExpanded = (entrantId) => {
        setExpandedHorses(prev => ({
            ...prev,
            [entrantId]: !prev[entrantId]
        }));
    };
    
    return (
        <Card id={raceCardId} className="bg-slate-900 border-slate-700 overflow-hidden">
            {/* Race Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-800/50 px-6 py-4 border-b border-amber-500/50">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h3 className="text-lg font-bold text-white">
                                Race {race.race_number} — {formatTime(race.race_time)}
                            </h3>
                            {race.stage_name && (
                                <span className="text-slate-400 text-sm">{race.stage_name}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <Badge 
                                className={`${
                                    isFinished 
                                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                        : 'bg-primary/20 text-primary border-primary/30'
                                }`}
                            >
                                {race.total_picks} picks
                            </Badge>
                            {isFinished && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                                    Finished
                                </Badge>
                            )}
                        </div>
                    </div>
                    {race.event_title && (
                        <p className="text-amber-400 font-medium">{race.event_title}</p>
                    )}
                </div>
            </div>
            
            <CardContent className="p-6">
                {/* Top 3 Picks Display */}
                <div className="flex justify-center gap-8 mb-6">
                    {[1, 0, 2].map((idx, displayIdx) => {
                        const pick = top3[idx];
                        if (!pick) return null;
                        
                        const positions = ['2ND PICK', '1ST PICK', '3RD PICK'];
                        const sizes = ['h-14 w-14', 'h-16 w-16', 'h-14 w-14'];
                        const textSizes = ['text-sm', 'text-base', 'text-sm'];
                        const isWinner = isFinished && winner?.entrant_id === pick.entrant_id;
                        const isCenter = displayIdx === 1;
                        
                        return (
                            <div key={pick.entrant_id} className={`text-center ${isCenter ? 'scale-110' : ''}`}>
                                <p className={`text-xs font-semibold mb-2 ${isCenter ? 'text-amber-400' : 'text-slate-400'}`}>
                                    {positions[displayIdx]}
                                </p>
                                <div className={`${sizes[displayIdx]} mx-auto mb-2 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border-2 ${isWinner ? 'border-green-500 ring-2 ring-green-500/30' : isCenter ? 'border-amber-500' : 'border-slate-600'}`}>
                                    {pick.silk_url ? (
                                        <img 
                                            src={pick.silk_url} 
                                            alt={pick.entrant_name}
                                            className="w-full h-full object-contain p-1"
                                        />
                                    ) : (
                                        <span className="text-lg font-bold">{pick.entrant_number || idx + 1}</span>
                                    )}
                                </div>
                                <p className={`font-semibold ${textSizes[displayIdx]} ${isWinner ? 'text-green-400' : 'text-white'}`}>
                                    {pick.entrant_name}
                                    {isWinner && <Trophy className="inline ml-1 h-3 w-3 text-amber-500" />}
                                </p>
                                {pick.sp && (
                                    <p className="text-xs text-amber-400 font-data">{pick.sp.toFixed(1)} SP</p>
                                )}
                                <p className={`text-xs ${isCenter ? 'text-amber-400' : 'text-slate-400'}`}>
                                    <span className={isCenter ? 'text-amber-400 font-bold' : 'text-primary font-bold'}>
                                        {pick.count}
                                    </span> picks ({pick.percentage}%)
                                </p>
                                {isCenter && (
                                    <div className="flex justify-center gap-1 mt-1">
                                        <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                                        <div className="w-6 h-2 rounded-full bg-amber-500"></div>
                                        <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* Shrewd Pick Badge */}
                {race.shrewd_pick && (
                    <div className="flex justify-center mb-4">
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 gap-1">
                            <Zap className="h-3 w-3" />
                            Shrewd Pick! {race.shrewd_pick.picked_by.join(', ')} picked winner at {race.shrewd_pick.percentage}%
                        </Badge>
                    </div>
                )}
                
                {/* Full Breakdown */}
                <Collapsible open={expanded} onOpenChange={onToggle}>
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" className="w-full justify-between text-slate-400 hover:text-white">
                            <span className="font-semibold">FULL BREAKDOWN</span>
                            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                        <Table className="mt-4">
                            <TableHeader>
                                <TableRow className="border-slate-700">
                                    <TableHead className="text-slate-400 w-12">#</TableHead>
                                    <TableHead className="text-slate-400">HORSE</TableHead>
                                    <TableHead className="text-slate-400 w-16">SP</TableHead>
                                    <TableHead className="text-slate-400 w-16">NO.</TableHead>
                                    <TableHead className="text-slate-400">POPULARITY</TableHead>
                                    <TableHead className="text-slate-400 text-right">PICKS</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {race.distribution?.map((item, idx) => {
                                    const isWinner = isFinished && winner?.entrant_id === item.entrant_id;
                                    const isPlaced = isFinished && race.results?.some(
                                        r => r.entrant_id === item.entrant_id && r.position <= 3
                                    );
                                    const isExpanded = expandedHorses[item.entrant_id];
                                    
                                    return (
                                        <React.Fragment key={item.entrant_id}>
                                            <TableRow 
                                                className={`border-slate-800 cursor-pointer hover:bg-slate-800/50 ${isWinner ? 'bg-green-500/10' : isPlaced ? 'bg-amber-500/5' : ''}`}
                                                onClick={() => toggleHorseExpanded(item.entrant_id)}
                                            >
                                                <TableCell>
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                                        idx < 3 
                                                            ? 'bg-primary/20 text-primary border-l-4 border-primary' 
                                                            : 'bg-slate-800 text-slate-400'
                                                    }`}>
                                                        {idx + 1}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        {item.silk_url ? (
                                                            <img 
                                                                src={item.silk_url}
                                                                alt=""
                                                                className="w-8 h-8 object-contain"
                                                            />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded bg-slate-700"></div>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            <span className={`font-medium ${isWinner ? 'text-green-400' : 'text-white'}`}>
                                                                {item.entrant_name}
                                                                {isWinner && <Trophy className="inline ml-2 h-4 w-4 text-amber-500" />}
                                                            </span>
                                                            {isExpanded ? (
                                                                <ChevronUp className="h-4 w-4 text-slate-400" />
                                                            ) : (
                                                                <ChevronDown className="h-4 w-4 text-slate-400" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {item.sp ? (
                                                        <span className="text-amber-400 font-data text-sm">{item.sp.toFixed(1)}</span>
                                                    ) : (
                                                        <span className="text-slate-600">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-slate-400">
                                                    {item.entrant_number || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                                                        <div 
                                                            className={`h-full rounded-full ${isWinner ? 'bg-green-500' : 'bg-primary'}`}
                                                            style={{ width: `${Math.max(item.percentage, 2)}%` }}
                                                        />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <span className={`font-bold ${isWinner ? 'text-green-400' : 'text-primary'}`}>
                                                        {item.count}
                                                    </span>
                                                    <span className="text-slate-400 ml-1">({item.percentage}%)</span>
                                                </TableCell>
                                            </TableRow>
                                            {/* Expanded picker details */}
                                            {isExpanded && item.pick_details && item.pick_details.length > 0 && (
                                                <TableRow className="border-slate-800 bg-slate-800/30">
                                                    <TableCell colSpan={5} className="py-3">
                                                        <div className="pl-12">
                                                            <p className="text-xs text-slate-400 mb-2 font-semibold">
                                                                Picked by {item.count} user{item.count !== 1 ? 's' : ''}:
                                                            </p>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                {item.pick_details.map((detail, detailIdx) => (
                                                                    <div 
                                                                        key={detailIdx}
                                                                        className="flex items-center justify-between bg-slate-900 rounded px-3 py-2"
                                                                    >
                                                                        <span className="text-sm text-white font-medium">
                                                                            {detail.user_name}
                                                                        </span>
                                                                        <span className="text-xs text-slate-400">
                                                                            {detail.was_changed ? (
                                                                                <span className="text-amber-400" title="Changed pick">
                                                                                    ↻ {formatDateTime(detail.change_time)}
                                                                                </span>
                                                                            ) : (
                                                                                formatDateTime(detail.pick_time)
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CollapsibleContent>
                </Collapsible>
            </CardContent>
        </Card>
    );
}

// As It Happened Table Component
function AsItHappenedTable({ data, races, title, showAllRaces = false, onExport }) {
    const tableRef = useRef(null);
    if (!data || data.length === 0) return null;
    
    const displayRaces = showAllRaces ? races : races?.filter(r => r.status === 'finished');
    
    const getPositionBadge = (position) => {
        if (position === 1) return 'bg-amber-500 text-black';
        if (position === 2) return 'bg-gray-400 text-black';
        if (position === 3) return 'bg-amber-700 text-white';
        return 'bg-slate-700 text-slate-300';
    };
    
    const getChangeIcon = (change) => {
        if (change > 0) return <ArrowUp className="h-3 w-3 text-green-500" />;
        if (change < 0) return <ArrowDown className="h-3 w-3 text-red-500" />;
        return <Minus className="h-3 w-3 text-slate-500" />;
    };

    const handleExportTable = async () => {
        if (!tableRef.current) return;
        
        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(tableRef.current, {
                backgroundColor: '#0f172a',
                scale: 2,
                useCORS: true,
                scrollX: 0,
                scrollY: 0,
                windowWidth: tableRef.current.scrollWidth + 100,
                windowHeight: tableRef.current.scrollHeight + 100
            });
            
            const link = document.createElement('a');
            link.download = `racewise-leaderboard-${showAllRaces ? 'overall' : 'daily'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Failed to export table:', err);
        }
    };
    
    return (
        <Card className="bg-slate-900 border-slate-700">
            <CardHeader className="border-b border-slate-700">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-primary" />
                            {title}
                        </CardTitle>
                        <CardDescription>
                            Track how positions changed after each race ({data.length} players, {displayRaces?.length || 0} races)
                        </CardDescription>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleExportTable}
                        className="gap-2"
                    >
                        <Download className="h-4 w-4" />
                        Export Table
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto" ref={tableRef}>
                    <Table className="min-w-max">
                        <TableHeader>
                            <TableRow className="border-slate-700 bg-slate-800/50">
                                <TableHead className="text-slate-400 sticky left-0 bg-slate-800 z-10 min-w-[50px] w-[50px]">
                                    #
                                </TableHead>
                                <TableHead className="text-slate-400 sticky left-[50px] bg-slate-800 z-10 min-w-[200px]">
                                    Player
                                </TableHead>
                                {displayRaces?.map((race, idx) => (
                                    <TableHead 
                                        key={race.event_id}
                                        className="text-slate-400 text-center min-w-[70px] px-2"
                                    >
                                        <div className="flex flex-col items-center">
                                            <span className="font-bold text-xs">R{race.race_number}</span>
                                            <span className="text-[10px] text-slate-500 whitespace-nowrap">
                                                {new Date(race.race_time).toLocaleTimeString('en-GB', { 
                                                    hour: '2-digit', 
                                                    minute: '2-digit' 
                                                })}
                                            </span>
                                        </div>
                                    </TableHead>
                                ))}
                                <TableHead className="text-slate-400 text-center min-w-[100px] bg-slate-800/80">
                                    Final
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.map((player, playerIdx) => (
                                <TableRow 
                                    key={player.user_id}
                                    className={`border-slate-800 ${playerIdx < 3 ? 'bg-slate-800/30' : ''}`}
                                >
                                    <TableCell className="sticky left-0 bg-slate-900 z-10 w-[50px]">
                                        <Badge className={`${getPositionBadge(player.final_position)} w-6 h-6 p-0 justify-center`}>
                                            {player.final_position}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="sticky left-[50px] bg-slate-900 z-10 font-medium min-w-[200px]">
                                        <div className="flex items-center gap-2 whitespace-nowrap">
                                            {player.final_position === 1 && <Crown className="h-4 w-4 text-amber-500 shrink-0" />}
                                            {player.final_position === 2 && <Medal className="h-4 w-4 text-gray-400 shrink-0" />}
                                            {player.final_position === 3 && <Medal className="h-4 w-4 text-amber-700 shrink-0" />}
                                            <span className="text-sm whitespace-nowrap">{player.user_name}</span>
                                        </div>
                                    </TableCell>
                                    {displayRaces?.map((race, raceIdx) => {
                                        const raceHistory = player.race_history?.find(h => h.event_id === race.event_id);
                                        const prevHistory = raceIdx > 0 
                                            ? player.race_history?.find(h => h.event_id === displayRaces[raceIdx - 1].event_id)
                                            : null;
                                        const positionChange = prevHistory 
                                            ? prevHistory.position - (raceHistory?.position || 0)
                                            : 0;
                                        
                                        return (
                                            <TableCell 
                                                key={race.event_id}
                                                className="text-center px-2"
                                            >
                                                <div className="flex flex-col items-center gap-1">
                                                    <div className="flex items-center gap-1">
                                                        <span className={`text-sm font-bold ${
                                                            raceHistory?.position <= 3 ? 'text-primary' : 'text-slate-300'
                                                        }`}>
                                                            {raceHistory?.position || '-'}
                                                        </span>
                                                        {raceIdx > 0 && positionChange !== 0 && getChangeIcon(positionChange)}
                                                    </div>
                                                    {raceHistory?.points_earned > 0 && (
                                                        <span className="text-xs text-green-400">
                                                            +{raceHistory.points_earned.toFixed(1)}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell className="text-center bg-slate-800/50">
                                        <div className="flex flex-col items-center">
                                            <span className="text-lg font-bold text-primary">
                                                {player.final_points?.toFixed(1) || '0.0'}
                                            </span>
                                            <span className="text-xs text-slate-400">pts</span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}

// Tournament Summary Card Component
function TournamentSummaryCard({ data, formatDate }) {
    const summaryRef = useRef(null);
    const summary = data.tournament_summary || {};
    const competition = data.competition || {};
    const [editMode, setEditMode] = useState(false);
    
    const handleExportSummary = async () => {
        if (!summaryRef.current) return;
        
        // Turn off edit mode before export so outlines don't show
        setEditMode(false);
        
        // Small delay to let React re-render without edit outlines
        await new Promise(r => setTimeout(r, 100));
        
        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(summaryRef.current, {
                backgroundColor: '#0f172a',
                scale: 2,
                useCORS: true
            });
            
            const link = document.createElement('a');
            link.download = `${competition.name || 'tournament'}-summary.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Failed to export summary:', err);
        }
    };
    
    const editableProps = editMode ? {
        contentEditable: true,
        suppressContentEditableWarning: true,
        style: { outline: '1px dashed rgba(0,255,210,0.4)', borderRadius: '4px', padding: '2px 4px', cursor: 'text' }
    } : {};
    
    return (
        <div className="space-y-6">
            <div className="flex justify-end gap-2">
                <Button 
                    variant={editMode ? "default" : "outline"} 
                    onClick={() => setEditMode(!editMode)} 
                    className="gap-2"
                    data-testid="toggle-edit-mode"
                >
                    <Pencil className="h-4 w-4" />
                    {editMode ? 'Editing' : 'Edit Text'}
                </Button>
                <Button variant="outline" onClick={handleExportSummary} className="gap-2" data-testid="export-summary-btn">
                    <Download className="h-4 w-4" />
                    Export Summary
                </Button>
            </div>
            
            <div ref={summaryRef} className="space-y-6 p-6 bg-gradient-to-br from-[#0a1628] via-[#0f1d35] to-[#0a1628] rounded-xl border border-[#1e3a5f]/50">
                {/* Tournament Header */}
                <div className="text-center border-b border-[#1e3a5f]/50 pb-6">
                    <div className="flex justify-center mb-4">
                        <img 
                            src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png" 
                            alt="Podium" 
                            className="h-14 object-contain"
                        />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">{competition.name || 'Tournament'}</h2>
                    <p className="text-[#00D4AA] text-lg">{competition.meeting_name || ''}</p>
                    {competition.start_date && competition.end_date && (
                        <p className="text-slate-400 mt-2" {...editableProps}>
                            {formatDate(competition.start_date)} - {formatDate(competition.end_date)}
                        </p>
                    )}
                </div>
                
                {/* Tournament Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#0f1d35]/80 rounded-lg p-4 text-center border border-[#1e3a5f]/50">
                        <span className="text-2xl block mb-2">👥</span>
                        <p className="text-3xl font-bold text-white">{data.total_entries}</p>
                        <p className="text-xs text-slate-400">Entrants</p>
                    </div>
                    <div className="bg-[#0f1d35]/80 rounded-lg p-4 text-center border border-[#1e3a5f]/50">
                        <span className="text-2xl block mb-2">🏇</span>
                        <p className="text-3xl font-bold text-white">{summary.total_races || 0}</p>
                        <p className="text-xs text-slate-400">Races</p>
                    </div>
                    <div className="bg-[#0f1d35]/80 rounded-lg p-4 text-center border border-[#1e3a5f]/50">
                        <span className="text-2xl block mb-2">⚡</span>
                        <p className="text-3xl font-bold text-white">{data.total_picks}</p>
                        <p className="text-xs text-slate-400">Total Picks</p>
                    </div>
                    <div className="bg-[#0f1d35]/80 rounded-lg p-4 text-center border border-[#1e3a5f]/50">
                        <span className="text-2xl block mb-2">🏆</span>
                        <p className="text-3xl font-bold text-white">{summary.total_winners || 0}</p>
                        <p className="text-xs text-slate-400">Winners Picked</p>
                    </div>
                </div>
                
                {/* Overall Tournament Winner */}
                {summary.overall_winner && (
                    <div className="bg-gradient-to-r from-[#1e3a5f]/40 via-[#00D4AA]/10 to-[#1e3a5f]/40 rounded-xl p-6 border border-[#00D4AA]/40">
                        <h3 className="text-center text-[#00D4AA] font-bold text-lg mb-4 flex items-center justify-center gap-2">
                            <span className="text-xl">👑</span>
                            TOURNAMENT CHAMPION
                            <span className="text-xl">👑</span>
                        </h3>
                        <div className="text-center">
                            <p className="text-4xl font-bold text-white mb-2">{summary.overall_winner.user_name}</p>
                            <p className="text-2xl text-[#00D4AA] font-data">{summary.overall_winner.final_points?.toFixed(1)} points</p>
                        </div>
                    </div>
                )}
                
                {/* Top 3 Podium - Redesigned */}
                {summary.overall_top3 && summary.overall_top3.length >= 3 && (
                    <div className="mt-8">
                        <h3 className="text-center text-slate-400 font-semibold mb-6 text-lg">FINAL PODIUM</h3>
                        <div className="flex justify-center items-end gap-3 md:gap-6">
                            {/* 2nd Place */}
                            <div className="flex flex-col items-center">
                                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center shadow-lg mb-3">
                                    <span className="text-2xl md:text-3xl font-bold text-white">2</span>
                                </div>
                                <div className="h-24 w-24 md:w-32 bg-gradient-to-t from-slate-500 to-slate-400 rounded-t-xl flex flex-col items-center justify-end pb-3">
                                    <span className="text-xl mb-1">🥈</span>
                                    <span className="text-white font-bold text-sm">2nd</span>
                                </div>
                                <div className="bg-[#0f1d35] w-24 md:w-32 p-3 rounded-b-xl border-x border-b border-[#1e3a5f]/50">
                                    <p className="font-bold text-white text-center text-sm">{summary.overall_top3[1]?.user_name}</p>
                                    <p className="text-gray-400 text-center text-xs mt-1">{summary.overall_top3[1]?.final_points?.toFixed(1)} pts</p>
                                </div>
                            </div>
                            
                            {/* 1st Place */}
                            <div className="flex flex-col items-center -mt-6">
                                <span className="text-3xl mb-2 animate-bounce">👑</span>
                                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[#00D4AA] to-[#00a88a] flex items-center justify-center shadow-xl shadow-[#00D4AA]/30 mb-3 ring-4 ring-[#00D4AA]/40">
                                    <span className="text-3xl md:text-4xl font-bold text-white">1</span>
                                </div>
                                <div className="h-32 w-28 md:w-36 bg-gradient-to-t from-[#00a88a] to-[#00D4AA] rounded-t-xl flex flex-col items-center justify-end pb-3">
                                    <span className="text-3xl mb-1">🏆</span>
                                    <span className="text-white font-bold">1st</span>
                                </div>
                                <div className="bg-[#0f1d35] w-28 md:w-36 p-4 rounded-b-xl border-x border-b border-[#00D4AA]/40">
                                    <p className="font-bold text-white text-center">{summary.overall_top3[0]?.user_name}</p>
                                    <p className="text-[#00D4AA] text-center font-data text-lg mt-1">{summary.overall_top3[0]?.final_points?.toFixed(1)} pts</p>
                                </div>
                            </div>
                            
                            {/* 3rd Place */}
                            <div className="flex flex-col items-center">
                                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#00a88a] to-[#1e3a5f] flex items-center justify-center shadow-lg mb-3">
                                    <span className="text-2xl md:text-3xl font-bold text-white">3</span>
                                </div>
                                <div className="h-20 w-24 md:w-32 bg-gradient-to-t from-[#1e3a5f] to-[#00a88a] rounded-t-xl flex flex-col items-center justify-end pb-3">
                                    <span className="text-xl mb-1">🥉</span>
                                    <span className="text-white font-bold text-sm">3rd</span>
                                </div>
                                <div className="bg-[#0f1d35] w-24 md:w-32 p-3 rounded-b-xl border-x border-b border-[#1e3a5f]/50">
                                    <p className="font-bold text-white text-center text-sm">{summary.overall_top3[2]?.user_name}</p>
                                    <p className="text-[#00a88a] text-center text-xs mt-1">{summary.overall_top3[2]?.final_points?.toFixed(1)} pts</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Fun Facts / Tournament Highlights */}
                {summary.fun_facts && summary.fun_facts.length > 0 && (
                    <div className="mt-8">
                        <h3 className="text-center text-[#00D4AA] font-semibold mb-4 tracking-wider">TOURNAMENT HIGHLIGHTS</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {summary.fun_facts.map((fact, idx) => (
                                <div key={idx} className="bg-gradient-to-br from-[#0f1d35] to-[#0a1628] rounded-xl p-4 border border-[#1e3a5f]/50 hover:border-[#00D4AA]/50 transition-colors">
                                    <p className="text-lg mb-2">{fact.title}</p>
                                    
                                    {fact.type === 'biggest_winner' && (
                                        <div>
                                            <p className="text-white font-bold text-xl">{fact.horse_name}</p>
                                            <p className="text-green-400 font-data text-2xl">{fact.odds?.toFixed(1)} SP</p>
                                            <p className="text-slate-400 text-sm mt-1">{fact.race_name}</p>
                                            <p className="text-primary text-xs">Picked by {fact.picked_by}</p>
                                            <p className="text-green-400 text-xs">+{fact.points_earned?.toFixed(1)} pts</p>
                                        </div>
                                    )}
                                    
                                    {fact.type === 'most_popular' && (
                                        <div>
                                            <p className="text-white font-bold text-xl">{fact.horse_name}</p>
                                            <p className="text-primary font-data text-2xl">{fact.percentage}%</p>
                                            <p className="text-slate-400 text-sm">picked by {fact.pick_count} people</p>
                                            <p className="text-slate-400 text-sm mt-1">{fact.race_name}</p>
                                            {fact.won ? (
                                                <Badge className="bg-green-500/20 text-green-400 mt-2">✓ Won!</Badge>
                                            ) : (
                                                <Badge className="bg-red-500/20 text-red-400 mt-2">✗ Lost</Badge>
                                            )}
                                        </div>
                                    )}
                                    
                                    {fact.type === 'most_winners' && (
                                        <div>
                                            <p className="text-white font-bold text-xl">{fact.user_name}</p>
                                            <p className="text-green-400 font-data text-2xl">{fact.winners} winners</p>
                                            <p className="text-slate-400 text-sm">from {fact.picks} picks</p>
                                        </div>
                                    )}
                                    
                                    {fact.type === 'best_day' && (
                                        <div>
                                            <p className="text-white font-bold text-xl">{fact.user_name}</p>
                                            <p className="text-amber-400 font-data text-2xl">+{fact.points?.toFixed(1)} pts</p>
                                            <p className="text-slate-400 text-sm">in a single day</p>
                                        </div>
                                    )}
                                    
                                    {fact.type === 'shrewd_pick' && (
                                        <div>
                                            <p className="text-white font-bold text-xl">{fact.horse_name}</p>
                                            <p className="text-purple-400 font-data text-2xl">Only {fact.percentage}%</p>
                                            <p className="text-slate-400 text-sm">picked the winner</p>
                                            <p className="text-slate-400 text-sm mt-1">{fact.race_name}</p>
                                            <p className="text-purple-400 text-xs">{fact.picked_by}</p>
                                        </div>
                                    )}
                                    
                                    {fact.type === 'most_seconds' && (
                                        <div>
                                            <p className="text-white font-bold text-xl">{fact.user_name}</p>
                                            <p className="text-amber-400 font-data text-2xl">{fact.second_places}x 2nd Place</p>
                                            <p className="text-slate-400 text-sm">So close to the win!</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Daily Winners */}
                {summary.daily_winners && summary.daily_winners.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-center text-[#00D4AA] font-semibold mb-4 tracking-wider">DAILY CHAMPIONS</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {summary.daily_winners.map((dw, idx) => (
                                <div key={dw.date} className="bg-[#0f1d35]/80 rounded-lg p-4 border border-[#1e3a5f]/50 text-center">
                                    <p className="text-xs text-slate-500 mb-1">Day {idx + 1}</p>
                                    <p className="text-sm text-[#00D4AA] mb-2">{formatDate(dw.date)}</p>
                                    <div className="flex justify-center mb-2">
                                        <span className="text-2xl">⭐</span>
                                    </div>
                                    <p className="font-bold text-white">{dw.user_name}</p>
                                    <p className="text-xs text-slate-400">{dw.points?.toFixed(1)} pts</p>
                                    {dw.bonus_points > 0 && (
                                        <p className="text-xs text-green-400">+{dw.bonus_points} bonus</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Footer */}
                <div className="text-center pt-4 border-t border-[#1e3a5f]/50">
                    <p className="text-slate-500 text-xs mb-2">Powered by</p>
                    <img 
                        src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png" 
                        alt="Podium" 
                        className="h-6 mx-auto object-contain opacity-60"
                    />
                </div>
            </div>
        </div>
    );
}
